import { z } from "zod";
import { FISCAL_EXTRACT_BLOCKS, fiscalExtractCellSchema, fiscalExtractSheetSchema, } from "./commercial-fiscal-extract.js";
import { environmentSchema } from "./context.js";
import { evidenceLocatorSchema } from "./evidence-locator.js";
export const WORKBOOK_ROW_DRAFT_MAX_ROWS = 71;
export const WORKBOOK_ROW_DRAFT_MAX_EVIDENCE_LINKS = 100;
const sha = z.string().regex(/^[a-f0-9]{64}$/);
const worksheetPart = z.string().regex(/^xl\/worksheets\/sheet[1-9]\d{0,3}\.xml$/);
/** This workspace is separate from executable rule-proposal drafts. */
export const workbookRowDraftBlockSchema = z.enum(["PURCHASE_ST", "SALE_ST"]);
export const workbookRowDraftSelectorSchema = z.discriminatedUnion("block", [
    z
        .object({
        block: z.literal("PURCHASE_ST"),
        row: z.number().int().min(11).max(24),
        recordSha256: sha,
    })
        .strict(),
    z
        .object({
        block: z.literal("SALE_ST"),
        row: z.number().int().min(72).max(128),
        recordSha256: sha,
    })
        .strict(),
]);
const requestBase = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    expectedExtractSha256: sha,
    rows: z.array(workbookRowDraftSelectorSchema).min(1).max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
})
    .strict();
export const workbookRowDraftRequestSchema = z
    .discriminatedUnion("mode", [
    requestBase.extend({ mode: z.literal("DRY_RUN") }),
    requestBase.extend({
        mode: z.literal("COMMIT"),
        expectedDraftBatchSha256: sha,
        confirmedDraftSelection: z.literal(true),
    }),
])
    .superRefine((value, context) => {
    const identities = new Set();
    for (const [index, row] of value.rows.entries()) {
        const identity = `${row.block}:${row.row}`;
        if (identities.has(identity))
            context.addIssue({
                code: "custom",
                path: ["rows", index],
                message: "Duplicate workbook row identity",
            });
        identities.add(identity);
    }
});
export const WORKBOOK_ROW_DRAFT_CORE_BLOCKERS = [
    "SOURCE_PROVENANCE_DECLARED",
    "OFFICIAL_EVIDENCE_REQUIRED",
    "LEGAL_INTERVAL_UNKNOWN",
    "CLASSIFICATION_UNRESOLVED",
    "PRODUCT_SCOPE_UNRESOLVED",
    "ROUTE_SCOPE_UNRESOLVED",
    "SUPPLIER_TAX_CONDITIONS_UNRESOLVED",
    "COVERAGE_COMPARISON_REQUIRED",
];
export const workbookRowDraftBlockerSchema = z.enum([
    ...WORKBOOK_ROW_DRAFT_CORE_BLOCKERS,
    "MISSING_VALUE",
    "UNKNOWN_UNIT",
    "FORMULA_NOT_EXECUTED",
    "CACHE_UNAVAILABLE",
]);
export const workbookRowDraftSourceSchema = z
    .object({
    extractId: z.string().uuid(),
    extractSha256: sha,
    parentWorkbookSha256: sha,
    profile: z.literal("COMMERCIAL_FISCAL_PARAMETERS_V1"),
    extractorVersion: z.literal("manual-fiscal-json-v1"),
    parentVerification: z.literal("DECLARED_UNVERIFIED"),
    layoutVerification: z.literal("DECLARED_PROFILE_MAPPING"),
    block: workbookRowDraftBlockSchema,
    row: z.number().int().min(11).max(128),
    recordSha256: sha,
    locators: z
        .array(z
        .object({
        sheet: fiscalExtractSheetSchema,
        worksheetPart,
        locator: evidenceLocatorSchema.safeExtend({ kind: z.literal("document") }),
    })
        .strict())
        .min(1)
        .max(2),
})
    .strict()
    .superRefine((source, context) => {
    const purchase = source.block === "PURCHASE_ST";
    if (source.row < (purchase ? 11 : 72) || source.row > (purchase ? 24 : 128))
        context.addIssue({ code: "custom", path: ["row"], message: "Row outside selected block" });
    const sheets = new Set();
    const parts = new Set();
    for (const [index, replica] of source.locators.entries()) {
        const baseSheet = purchase ? "Parâmetros Compra" : "Parâmetros Venda";
        if (replica.sheet.replace(/ \(2\)$/u, "") !== baseSheet ||
            sheets.has(replica.sheet) ||
            parts.has(replica.worksheetPart) ||
            replica.locator.value !== `xlsx:${replica.worksheetPart}#cell=A${source.row}`)
            context.addIssue({
                code: "custom",
                path: ["locators", index],
                message: "Replica must identify the exact selected workbook row",
            });
        sheets.add(replica.sheet);
        parts.add(replica.worksheetPart);
    }
});
export const workbookRowDraftSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    draftId: z.string().uuid().nullable(),
    createdAt: z.string().datetime().nullable(),
    createdBy: z.string().uuid().nullable(),
    environment: environmentSchema,
    origin: z.literal("WORKBOOK"),
    status: z.literal("BLOCKED"),
    canSubmit: z.literal(false),
    executable: z.literal(false),
    proposalId: z.null(),
    source: workbookRowDraftSourceSchema,
    observations: z.array(fiscalExtractCellSchema).min(2).max(13),
    blockers: z.array(workbookRowDraftBlockerSchema).min(8).max(12),
    legalValidity: z.literal("UNKNOWN"),
    effectiveFrom: z.null(),
    effectiveTo: z.null(),
})
    .strict()
    .superRefine((draft, context) => {
    const fields = FISCAL_EXTRACT_BLOCKS[draft.source.block]?.fields ?? {};
    const columns = new Set();
    const required = new Set(WORKBOOK_ROW_DRAFT_CORE_BLOCKERS);
    let typed = false;
    let invalid = draft.observations.length !== Object.keys(fields).length;
    for (const cell of draft.observations) {
        const observation = cell.observation;
        invalid ||= columns.has(cell.column) || !fields[cell.column];
        columns.add(cell.column);
        if (observation.kind === "MISSING")
            required.add("MISSING_VALUE");
        const value = observation.kind === "FORMULA"
            ? observation.cache.state === "VALUE"
                ? observation.cache.value
                : null
            : observation;
        if (observation.kind === "FORMULA") {
            required.add("FORMULA_NOT_EXECUTED");
            if (!value)
                required.add("CACHE_UNAVAILABLE");
        }
        if (value?.kind === "DECIMAL" && value.unit === "UNKNOWN")
            required.add("UNKNOWN_UNIT");
        if (value && value.kind !== "MISSING") {
            invalid ||= value.kind !== fields[cell.column]?.kind;
            typed ||= value.kind !== "HASH_ONLY";
        }
    }
    if (invalid || !typed)
        context.addIssue({
            code: "custom",
            path: ["observations"],
            message: "Concrete rows require the exact declared column kinds and a typed observation",
        });
    if (new Set(draft.blockers).size !== draft.blockers.length ||
        [...required].some((blocker) => !draft.blockers.some((present) => present === blocker)))
        context.addIssue({
            code: "custom",
            path: ["blockers"],
            message: "All core and observation blockers must remain present and unique",
        });
    const identities = [draft.draftId, draft.createdAt, draft.createdBy];
    if (identities.some((value) => value === null) && identities.some((value) => value !== null))
        context.addIssue({
            code: "custom",
            path: ["draftId"],
            message: "Preview identity fields must all be null; stored identity fields must all exist",
        });
});
export const workbookRowDraftBatchResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: z.enum(["DRY_RUN", "COMMIT"]),
    environment: environmentSchema,
    extractId: z.string().uuid(),
    extractSha256: sha,
    draftBatchSha256: sha,
    persistable: z.literal(true),
    drafts: z.array(workbookRowDraftSchema).max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
    counts: z
        .object({
        selectedRows: z.number().int().min(1).max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
        newDrafts: z.number().int().nonnegative().max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
        reusedDrafts: z.number().int().nonnegative().max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
        blockedDrafts: z.number().int().nonnegative().max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
        approvedProposals: z.literal(0),
        publishedRules: z.literal(0),
    })
        .strict(),
})
    .strict()
    .superRefine((batch, context) => {
    if (batch.counts.blockedDrafts !== batch.drafts.length ||
        batch.counts.selectedRows !== batch.drafts.length ||
        new Set(batch.drafts.map((draft) => `${draft.source.block}:${draft.source.row}`)).size !==
            batch.drafts.length ||
        (batch.mode === "DRY_RUN"
            ? batch.counts.newDrafts !== 0 || batch.counts.reusedDrafts !== 0
            : batch.counts.newDrafts + batch.counts.reusedDrafts !== batch.drafts.length) ||
        batch.drafts.some((draft) => draft.environment !== batch.environment ||
            draft.source.extractId !== batch.extractId ||
            draft.source.extractSha256 !== batch.extractSha256 ||
            (batch.mode === "COMMIT" ? draft.draftId === null : draft.draftId !== null)))
        context.addIssue({
            code: "custom",
            path: ["drafts"],
            message: "An atomic batch must account for every selected row in its own source context",
        });
});
export const workbookRowDraftEvidencePurposeSchema = z.enum([
    "CLASSIFICATION",
    "ST_APPLICABILITY",
    "MVA",
    "ROUTE_SCOPE",
    "LEGAL_INTERVAL",
    "OTHER_FISCAL",
]);
const evidenceLinkRequestBase = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    evidenceId: z.string().uuid(),
    expectedContentSha256: sha,
    locator: evidenceLocatorSchema,
    purpose: workbookRowDraftEvidencePurposeSchema,
})
    .strict();
export const workbookRowDraftEvidenceLinkRequestSchema = z.discriminatedUnion("mode", [
    evidenceLinkRequestBase.extend({ mode: z.literal("DRY_RUN") }),
    evidenceLinkRequestBase.extend({
        mode: z.literal("COMMIT"),
        expectedLinkSha256: sha,
        confirmedEvidenceLink: z.literal(true),
    }),
]);
export const workbookRowDraftEvidenceLinkResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: z.enum(["DRY_RUN", "COMMIT"]),
    linkId: z.string().uuid().nullable(),
    createdAt: z.string().datetime().nullable(),
    createdBy: z.string().uuid().nullable(),
    draftId: z.string().uuid(),
    environment: environmentSchema,
    linkSha256: sha,
    evidenceId: z.string().uuid(),
    sourceId: z.string().uuid(),
    sourceTier: z.literal(1),
    contentSha256: sha,
    locator: evidenceLocatorSchema,
    purpose: workbookRowDraftEvidencePurposeSchema,
    documentReviewAtLink: z.literal("APPROVED"),
    authorityAssessment: z.literal("UNASSESSED"),
    status: z.literal("RECORDED_FOR_REVIEW"),
    legalValidity: z.literal("UNKNOWN"),
    executable: z.literal(false),
    unblocksDraft: z.literal(false),
    reused: z.boolean(),
})
    .strict()
    .superRefine((link, context) => {
    const identities = [link.linkId, link.createdAt, link.createdBy];
    if ((identities.some((value) => value === null) && identities.some((value) => value !== null)) ||
        (link.mode === "COMMIT" && link.linkId === null))
        context.addIssue({
            code: "custom",
            path: ["linkId"],
            message: "A stored link requires complete server identity fields",
        });
});
export const workbookRowDraftListSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    environment: environmentSchema,
    extractId: z.string().uuid(),
    drafts: z.array(workbookRowDraftSchema).max(WORKBOOK_ROW_DRAFT_MAX_ROWS),
})
    .strict()
    .superRefine((list, context) => {
    if (new Set(list.drafts.map((draft) => draft.draftId)).size !== list.drafts.length ||
        list.drafts.some((draft) => draft.draftId === null ||
            draft.environment !== list.environment ||
            draft.source.extractId !== list.extractId))
        context.addIssue({
            code: "custom",
            path: ["drafts"],
            message: "List entries must be distinct retained drafts in the requested context",
        });
});
export const workbookRowDraftDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    draft: workbookRowDraftSchema,
    links: z
        .array(workbookRowDraftEvidenceLinkResultSchema)
        .max(WORKBOOK_ROW_DRAFT_MAX_EVIDENCE_LINKS),
})
    .strict()
    .superRefine((detail, context) => {
    if (detail.draft.draftId === null ||
        new Set(detail.links.map((link) => link.linkId)).size !== detail.links.length ||
        detail.links.some((link) => link.mode !== "COMMIT" ||
            link.linkId === null ||
            link.draftId !== detail.draft.draftId ||
            link.environment !== detail.draft.environment))
        context.addIssue({
            code: "custom",
            path: ["links"],
            message: "Detail requires distinct retained links belonging to its retained draft",
        });
});
//# sourceMappingURL=workbook-row-drafts.js.map